import { Route, NavigationGuardNext } from 'vue-router';
import {
  currentUserIsParent,
  currentUserIsSitter,
  isVerifyEmailPath,
  resolvePostVerifyDestination,
} from '@/apps/verifyEmail/helpers/paths';
import { resolveEmailVerificationGate } from '@/apps/verifyEmail/helpers/emailVerificationGate';
import { resolveParentEmailVerificationGate } from '@/apps/verifyEmail/helpers/parentEmailVerificationGate';
import {
  getEmailVerified,
  getEmailVerificationStatus,
  invalidateEmailVerificationStatus,
} from '@/apps/verifyEmail/helpers/emailVerificationStatus';
import {
  clearEmailVerificationRequired,
  getPendingEmailVerificationReturnPath,
  markEmailVerificationRequired,
} from '@/apps/verifyEmail/helpers/pendingEmailVerification';
import {
  EmailVerificationMoment,
  getEmailVerificationPlacement,
  isFullSitterOnboardingPath,
  PLACEMENT_NO_VERIFICATION_REQUIRED,
} from '@/apps/verifyEmail/helpers/placementPolicy';
import {
  getParentEmailVerificationPlacement,
  PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED,
} from '@/apps/verifyEmail/helpers/parentPlacementPolicy';
import {
  getAndSyncAdditionalProfile,
  isSitterOnboardingEmailVerificationPath,
} from '@/lib/sitterOnboarding/sitterOnboardingState';

/**
 * Redirects already-verified users away from /verify-email before the page mounts,
 * so they never see the waiting UI flash.
 */
export const redirectIfEmailAlreadyVerified = async (to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
  try {
    const verified = await getEmailVerified();
    if (verified) {
      invalidateEmailVerificationStatus();
      const pendingReturnPath = getPendingEmailVerificationReturnPath();
      clearEmailVerificationRequired();
      next(resolvePostVerifyDestination(to.query, currentUserIsSitter(), pendingReturnPath));
      return;
    }
  } catch (error) {
    console.error('Error checking email verification status in route guard:', error);
  }

  next();
};

/**
 * Shared checkpoint: redirect unverified sitters to /verify-email for the given moment.
 * Variant A uses onboarding; Variant B uses parent_interaction. Additional-profile
 * sitters skip the onboarding gate (they are not in first-time registration).
 */
export const redirectIfEmailVerificationRequired = async (
  moment: EmailVerificationMoment,
  to: Route,
  _from: Route,
  next: NavigationGuardNext
): Promise<void> => {
  if (isVerifyEmailPath(to.path)) {
    next();
    return;
  }

  if (moment === 'onboarding' && getAndSyncAdditionalProfile(to.query)) {
    next();
    return;
  }

  const decision = await resolveEmailVerificationGate({
    moment,
    returnPath: to.fullPath,
  });

  if (decision.requireVerification) {
    markEmailVerificationRequired(to.fullPath);
    next(decision.redirectTo);
    return;
  }

  next();
};

/** Onboarding checkpoint (Variant A when placement=registration). */
export const redirectIfEmailVerificationRequiredForOnboarding = (
  to: Route,
  from: Route,
  next: NavigationGuardNext
): Promise<void> => redirectIfEmailVerificationRequired('onboarding', to, from, next);

/**
 * Global onboarding gate: one router-level check for SC-Web signup routes and legacy
 * onboarding paths (e.g. IDV) reached via Vue Router, instead of per-child beforeEnter.
 */
export const emailVerificationOnboardingBeforeEach = (to: Route, from: Route, next: NavigationGuardNext): Promise<void> => {
  if (!isSitterOnboardingEmailVerificationPath(to.path)) {
    next();
    return Promise.resolve();
  }

  return redirectIfEmailVerificationRequired('onboarding', to, from, next);
};

/**
 * Message-center checkpoint (Variant B when placement=parent_interaction).
 * Wire on /messages beforeEnter so unverified sitters cannot open the inbox; nav tile stays visible.
 * Variant A sitters who reach here unverified are also blocked as a fallback. Variant C is not.
 */
export const redirectIfEmailVerificationRequiredForParentInteraction = (
  to: Route,
  from: Route,
  next: NavigationGuardNext
): Promise<void> => redirectIfEmailVerificationRequired('parent_interaction', to, from, next);

/**
 * Parent onboarding checkpoint (when placement=parent_onboarding).
 * Wire on registration care-type so employer eligibility always completes first.
 */
export const redirectIfParentEmailVerificationRequiredForOnboarding = async (
  to: Route,
  _from: Route,
  next: NavigationGuardNext
): Promise<void> => {
  if (isVerifyEmailPath(to.path)) {
    next();
    return;
  }

  const decision = await resolveParentEmailVerificationGate({
    moment: 'onboarding',
    returnPath: to.fullPath,
  });

  if (decision.requireVerification) {
    markEmailVerificationRequired(to.fullPath);
    next(decision.redirectTo);
    return;
  }

  next();
};

/**
 * Parent dashboard hard-gate (when placement=dashboard).
 * Redirects unverified parents to /verify-email before the dashboard mounts.
 */
export const redirectIfParentEmailVerificationRequiredForDashboard = async (
  to: Route,
  _from: Route,
  next: NavigationGuardNext
): Promise<void> => {
  if (isVerifyEmailPath(to.path)) {
    next();
    return;
  }

  const decision = await resolveParentEmailVerificationGate({
    moment: 'dashboard',
    returnPath: to.fullPath,
  });

  if (decision.requireVerification) {
    markEmailVerificationRequired(to.fullPath);
    next(decision.redirectTo);
    return;
  }

  next();
};

/**
 * Sticky trap: only when leaving /verify-email. Stay on the page until verified
 * (next(false)). Status-query failures fail-open so a blip does not brick the user.
 */
export const trapIfEmailVerificationPending = async (to: Route, from: Route, next: NavigationGuardNext): Promise<void> => {
  const leavingVerifyEmail = isVerifyEmailPath(from.path) && !isVerifyEmailPath(to.path) && !to.meta?.skipAuth;
  if (!leavingVerifyEmail) {
    next();
    return;
  }

  const isSitter = currentUserIsSitter();
  const isParent = currentUserIsParent();
  if (!isSitter && !isParent) {
    clearEmailVerificationRequired();
    next();
    return;
  }

  try {
    const status = await getEmailVerificationStatus();

    if (isSitter) {
      // Express exclusion can still be true while full-onboarding paths force GB evaluation.
      // Only skip the trap when exclusion applies to the pending return path.
      const pendingReturnPath = getPendingEmailVerificationReturnPath();
      if (status.excludeFromEmailVerificationExperiment && !isFullSitterOnboardingPath(pendingReturnPath)) {
        clearEmailVerificationRequired();
        next();
        return;
      }

      if (getEmailVerificationPlacement() === PLACEMENT_NO_VERIFICATION_REQUIRED) {
        clearEmailVerificationRequired();
        next();
        return;
      }
    } else if (
      !status.emailVerificationRequired ||
      getParentEmailVerificationPlacement() === PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED
    ) {
      // Existing soft-banner cohort (or control) should never stay trapped on /verify-email.
      clearEmailVerificationRequired();
      next();
      return;
    }

    if (status.verified) {
      invalidateEmailVerificationStatus();
      clearEmailVerificationRequired();
      next();
      return;
    }
  } catch (error) {
    // Fail-open on status errors — same posture as resolveEmailVerificationGate.
    console.error('Error checking email verification status for sticky trap:', error);
    next();
    return;
  }

  next(false);
};
